import { apiUrl } from "../utils/api";
import { oeffneEventSource } from "../utils/sseUrl";
import { BEARER_TOKEN_KEY } from "../utils/auth-client";

/**
 * Generischer Echtzeit-Envelope (Spiegel des Backend-`RealtimeEvent`).
 * `channel` gruppiert nach fachlichem Strom; FE-Konsumenten abonnieren je Channel.
 */
// `reconnect` ist ein CLIENT-synthetischer Kanal (B8): RealtimeContext feuert ihn
// nach einem SSE-Wiederaufbau, damit Konsumenten verpasste Frames heilen können
// (Caches invalidieren + neu laden). Er kommt NIE vom Server.
export type RealtimeChannel =
    | "notification"
    | "note"
    | "call"
    | "mail"
    | "calendar"
    | "ping"
    | "reconnect";

export interface RealtimeEvent<TPayload = unknown> {
  tenantId: string;
  userId: string;
  channel: RealtimeChannel;
  type: string;
  payload: TPayload;
}

/**
 * Nutzlast eines `mail`-Frames (T1, Spiegel des Backend-`mail-delta`). Bewusst
 * METADATENARM (kein Betreff/Absender) — der Konsument re-fetcht über den
 * sichtbarkeits-gefilterten Lesepfad.
 */
export interface MailRealtimePayload {
  mailbox: string;
  folder: string;
  messageId: string;
  changeType: "added" | "changed" | "removed";
  isRead: boolean;
}

/**
 * Nutzlast eines `calendar`-Frames (Termine-SP8, Spiegel des Backend-
 * `CalendarWebhookService`). BEWUSST metadatenarm — nur das betroffene Postfach;
 * der Konsument re-fetcht den sichtbaren Range über den sichtbarkeits-
 * gefilterten Lesepfad. KEIN Event-/Serien-Payload (Kalender ohne Delta-Store).
 */
export interface CalendarRealtimePayload {
  mailbox: string;
}

export const realtimeService = {
  /**
   * Öffnet den generischen SSE-Stream. EventSource kann keinen Authorization-
   * Header setzen, daher wird der Bearer — falls vorhanden — als ?access_token=…
   * übergeben (der AuthzGuard akzeptiert ihn aus der Query).
   *
   * WICHTIG (SSO-Fix): Microsoft-SSO-Sessions haben KEINEN Bearer in
   * localStorage (das Cookie ist die Session; #366 löscht stale Tokens sogar).
   * Früher lieferte diese Funktion dann `null` → SSO-User bekamen NIE einen
   * Realtime-Stream (keine Mail-Frames, keine Glocke — still). EventSource
   * sendet same-origin-Cookies automatisch, `getSession()` akzeptiert die
   * Cookie-Session → ohne Token einfach OHNE access_token verbinden.
   */
  openEventStream(): EventSource {
    const token = localStorage.getItem(BEARER_TOKEN_KEY);
    return oeffneEventSource(
      token ? apiUrl("realtime/events", { access_token: token }) : apiUrl("realtime/events"),
    );
  },
};
